import Button from "components/atoms/buttons/Button";
import Label from "components/atoms/forms/Label";
import { CloseIcon } from "components/atoms/Icon";
import Modal from "components/atoms/modal";
import InputFieldWithLabel from "components/molecules/forms/InputFieldWithLabel";
import SelectElement from "components/molecules/forms/SelectElement";
import _ from "lodash";
import { useDataProviderContext } from "pages/MagnetDetails/DataProvider";
import { useEffect, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import Select, { components, OptionProps, StylesConfig } from "react-select";

const AirdropActivationStatusLabelMap = {
  0: "Draft",
  1: "Scheduling",
  3: "Scheduled",
  4: "Started",
  5: "Airdropped",
  6: "Closed",
};

type Props = {
  onClose?: any;
  open: boolean;
};

const colourStyles: StylesConfig<Memento> = {
  control: (styles) => ({
    ...styles,
    backgroundColor: "#060F1B",
    padding: "9px 0",
    border: "1px solid white",
    ":hover": {
      border: "1px solid white",
    },
    ":focus": {
      border: "2px solid white",
    },
    ":active": {
      border: "2px solid white",
    },
  }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: "#1f2732",
  }),
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    // eslint-disable-next-line no-nested-ternary
    backgroundColor: isSelected ? "blue" : isFocused ? "#383f49" : undefined,
    color: "white",
  }),
  multiValue: (styles) => ({
    ...styles,
    backgroundColor: "#1f2732",
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    color: "white",
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    color: "white",
    opacity: "0.3",
    transition: "all 0.3s",
    ":hover": {
      opacity: "1",
    },
  }),
};

// const options = [
//   { label: "Count", value: "MIN_PARTICIPATE_COUNT" },
//   { label: "In", value: "PARTICIPATE_IN" },
// ];
const options = [
  { value: "MIN_PARTICIPATED_COUNT", display: "Minimum Participation Count" },
  { value: "PARTICIPATED_IN", display: "Participation In" },
];
const mementoOptions = [
  { display: "Share Request", value: "social_share" },
  { display: "Poll", value: "poll" },
];

function CustomOption(props: OptionProps<Memento>) {
  const { data } = props;
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <components.Option {...props} data={data}>
      <div className="flex gap-2 font-satoshi">
        <img className="inline-block aspect-1 h-12 object-cover" src={data.thumbnail_url} alt="" />
        <div className="flex flex-col">
          <span className="font-semibold">{data.name}</span>
          {/* @ts-ignore */}
          <span className="text-xxs opacity-75">{AirdropActivationStatusLabelMap[data.status]}</span>
        </div>
      </div>
    </components.Option>
  );
}

export default function RuleModal({ onClose, open }: Props) {
  const { register, watch, control, setValue } = useFormContext<MementoFormType>();
  const { fields: conditions, append, remove } = useFieldArray({ control, name: "incentive_rules.conditions" });
  const [groupedOptions, setGroupedOptions] = useState<any>();
  const { magnetMementos } = useDataProviderContext();

  useEffect(() => {
    if (magnetMementos?.length) {
      const optionMap = _.groupBy(magnetMementos, "activation_key");
      const pollsAndShareRequests = [
        { label: "Polls", options: optionMap.poll || [] },
        { label: "Share Requests", options: optionMap.social_share || [] },
      ];
      setGroupedOptions(pollsAndShareRequests);
    }
  }, [magnetMementos]);

  const handleAddCondition = () => {
    append({ action_type: "MIN_PARTICIPATED_COUNT", value: 1, activation_key: "social_share" });
    setValue("incentive_rules.operator", "AND");
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div className="relative w-[900px] space-y-6 rounded-2xl bg-primary p-4 text-white shadow-modal">
        <div>
          <div className="flex justify-between text-white">
            <section className=" w-[95%] font-sora text-xl font-semibold xl:text-2xl">Conditions For Drop</section>
            <button onClick={onClose} className="my-auto cursor-pointer" type="button">
              <CloseIcon className="h-5 w-5 text-white" />
            </button>
          </div>
          <p className="font-satoshi text-lg font-medium opacity-50">
            Please specify the conditions your users need to meet in order to grab this incentive:
          </p>
        </div>
        {/* Radio buttons */}
        <div>
          {conditions.map((condition, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={idx + condition.action_type} className="relative">
              {idx !== 0 && (
                <div className="relative ml-auto mr-4 w-24">
                  <SelectElement
                    label="Operator"
                    id="incentive_rules.operator"
                    register={register}
                    additionalClasses="text-base pl-2 pr-8"
                    placeHolder="Select a Condition"
                    selectedValue={watch("incentive_rules.operator")}
                    optionsLoading={false}
                    errorMessage=""
                    options={["AND", "OR"]}
                  />
                </div>
              )}
              <div className="flex items-center justify-between gap-8 pr-2">
                <div className="flex grow gap-4">
                  <SelectElement
                    label="Participation Condition"
                    id={`incentive_rules.conditions.${idx}.action_type`}
                    register={register}
                    additionalClasses="text-base pl-2 pr-8"
                    placeHolder="Select a Condition"
                    selectedValue={watch(`incentive_rules.conditions.${idx}.action_type`)}
                    optionsLoading={false}
                    errorMessage=""
                    options={options}
                  />
                  {watch(`incentive_rules.conditions.${idx}.action_type`) === "MIN_PARTICIPATED_COUNT" ? (
                    <>
                      <SelectElement
                        label="Memento"
                        id={`incentive_rules.conditions.${idx}.activation_key`}
                        register={register}
                        additionalClasses="text-xxs pl-2 pr-10"
                        placeHolder="Memento"
                        selectedValue={watch(`incentive_rules.conditions.${idx}.activation_key`)}
                        optionsLoading={false}
                        errorMessage=""
                        options={mementoOptions}
                      />
                      <InputFieldWithLabel
                        label="Minimum Count"
                        type="number"
                        placeHolder="Enter Minimum Count"
                        register={register}
                        id={`incentive_rules.conditions.${idx}.value`}
                        errorMessage=""
                      />
                    </>
                  ) : (
                    <div className="mt-1">
                      <Label htmlFor="mementos" label="Select Mementos" />
                      <Select
                        className="w-[400px] rounded-md font-satoshi font-450 text-white placeholder:text-white placeholder:opacity-70 focus:border-white focus:outline-none focus:ring-white "
                        options={groupedOptions as any}
                        getOptionValue={(memento: Memento) => memento.id}
                        getOptionLabel={(memento: Memento) => memento.name}
                        components={{ Option: CustomOption }}
                        isMulti
                        closeMenuOnSelect={false}
                        id={`incentive_rules.conditions.${idx}.value`}
                        onChange={(selectedValue) => {
                          // @ts-ignore
                          setValue(`incentive_rules.conditions.${idx}.value`, selectedValue);
                        }}
                        // @ts-ignore
                        value={watch(`incentive_rules.conditions.${idx}.value`)}
                        isClearable
                        isSearchable
                        styles={colourStyles}
                        placeholder="Select mementos (multiple allowed)"
                        defaultValue={[]}
                      />
                    </div>
                  )}
                </div>
                <button onClick={() => remove(idx)} type="button" className="mt-4 opacity-60 hover:opacity-80">
                  <CloseIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
        <Button
          text="+ Rule"
          disabled={watch("incentive_rules.conditions")?.length === 2}
          variant="rustPinkSolid"
          additionalClasses="text-xxs ml-auto disabled:hidden"
          onClick={handleAddCondition}
        />
        <div className="flex justify-end gap-4 pb-2">
          <Button text="DONE" variant="magnetiqSolid" onClick={onClose} />
        </div>
      </div>
    </Modal>
  );
}
